/**
 * Landing Page Content Service
 * Handles learning objectives, FAQs, testimonials, bonuses, and event landing page builder settings
 */

import { supabase, supabaseAnon } from '../lib/supabase';
import * as eventsService from './eventsService';
import { ensureCanManageEvent } from './utils';

/** Default landing page config (theme + density + hero style). */
export const DEFAULT_LANDING_CONFIG = {
  theme: 'prosperity',
  density: 'full',
  hero_style: 'form',
};

const VALID_THEMES = new Set(['prosperity', 'mujeres', 'fintech']);
const VALID_DENSITIES = new Set(['compact', 'full']);
const VALID_HERO_STYLES = new Set(['form', 'image_cta']);

/** Normalize persisted landing page row to { theme, density, hero_style }. */
export function normalizeLandingConfig(row) {
  const base = { ...DEFAULT_LANDING_CONFIG };
  if (!row || typeof row !== 'object') return base;

  const theme = row.theme ?? row.settings?.visual?.theme;
  const density = row.density ?? row.settings?.density;
  const heroStyle = row.hero_style ?? row.settings?.visual?.hero_style;

  if (theme && VALID_THEMES.has(theme)) base.theme = theme;
  if (density && VALID_DENSITIES.has(density)) base.density = density;
  if (heroStyle && VALID_HERO_STYLES.has(heroStyle)) base.hero_style = heroStyle;

  return base;
}

/** @deprecated Use normalizeLandingConfig — kept for transitional imports. */
export const DEFAULT_LANDING_SETTINGS = DEFAULT_LANDING_CONFIG;

/** @deprecated Use normalizeLandingConfig */
export function mergeLandingSettings(persisted) {
  return normalizeLandingConfig(
    persisted && typeof persisted === 'object' && !persisted.theme && persisted.hero
      ? { theme: 'prosperity', density: 'full' }
      : persisted
  );
}

/** Flatten RPC session rows `{ session, instructors }` to plain session objects. */
export function normalizeLandingSessions(sessions) {
  if (!Array.isArray(sessions)) return [];
  return sessions
    .map((row) => {
      if (row && typeof row === 'object' && row.session && typeof row.session === 'object') {
        return row.session;
      }
      return row;
    })
    .filter(Boolean)
    .sort((a, b) => {
      const ta = a?.start_time ? new Date(a.start_time).getTime() : 0;
      const tb = b?.start_time ? new Date(b.start_time).getTime() : 0;
      return ta - tb;
    });
}

/** Normalize get_event_landing_data RPC payload for template rendering. */
export function normalizeLandingRpcData(rpcData = {}) {
  if (!rpcData || typeof rpcData !== 'object') {
    return {
      sessions: [],
      learning_objectives: [],
      faqs: [],
      testimonials: [],
      bonuses: [],
      instructors: []
    };
  }
  return {
    sessions: normalizeLandingSessions(rpcData.sessions),
    learning_objectives: Array.isArray(rpcData.learning_objectives) ? rpcData.learning_objectives : [],
    faqs: Array.isArray(rpcData.faqs) ? rpcData.faqs : [],
    testimonials: Array.isArray(rpcData.testimonials) ? rpcData.testimonials : [],
    bonuses: Array.isArray(rpcData.bonuses) ? rpcData.bonuses : [],
    instructors: Array.isArray(rpcData.instructors) ? rpcData.instructors : []
  };
}

// ============ Event Landing Page Config ============

/**
 * Get event landing page settings (public - for rendering; RLS allows when event is public)
 * @param {string} eventId
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function getEventLandingPage(eventId) {
  if (!eventId) return { data: null, error: { message: 'Event ID is required.' } };
  try {
    const { data, error } = await supabase
      .from('event_landing_pages')
      .select('*')
      .eq('event_id', eventId)
      .maybeSingle();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getEventLandingPage error:', error);
    return { data: null, error };
  }
}

/**
 * Get event landing page settings (public, anon-safe — no auth required).
 * Uses supabaseAnon so it works on mobile browsers and custom domains
 * where the visitor has no auth session.
 * @param {string} eventId
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function getPublicEventLandingPage(eventId) {
  if (!eventId) return { data: null, error: { message: 'Event ID is required.' } };
  try {
    const { data, error } = await supabaseAnon
      .from('event_landing_pages')
      .select('*')
      .eq('event_id', eventId)
      .maybeSingle();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getPublicEventLandingPage error:', error);
    return { data: null, error };
  }
}

/**
 * Get event landing page for editor (with access check)
 * @param {string} eventId
 * @param {{ role?: string, userId?: string }} [options]
 */
export async function getEventLandingPageForEditor(eventId, options = {}) {
  const access = await ensureCanManageEvent(eventId);
  if (!access.allowed) {
    return { data: null, error: access.error };
  }
  return getEventLandingPage(eventId);
}

/**
 * Upsert event landing page config (theme + density + hero_style)
 * @param {string} eventId
 * @param {{ theme?: string, density?: string, hero_style?: string }} config
 * @param {{ role?: string, userId?: string }} [options]
 */
export async function upsertEventLandingPage(eventId, config, options = {}) {
  const access = await ensureCanManageEvent(eventId);
  if (!access.allowed) {
    return { data: null, error: access.error };
  }
  const normalized = normalizeLandingConfig(config);
  try {
    const payload = {
      event_id: eventId,
      theme: normalized.theme,
      density: normalized.density,
      hero_style: normalized.hero_style,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from('event_landing_pages')
      .upsert(payload, {
        onConflict: 'event_id',
        ignoreDuplicates: false,
      })
      .select()
      .single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] upsertEventLandingPage error:', error);
    return { data: null, error };
  }
}

/**
 * Upload image to landing page storage
 * @param {string} eventId
 * @param {File} file
 * @param {string} folder - e.g. 'hero', 'instructors', 'form'
 * @returns {Promise<{data: { url: string }|null, error: Error|null}>}
 */
export async function uploadLandingPageImage(eventId, file, folder = 'general') {
  const access = await ensureCanManageEvent(eventId);
  if (!access.allowed) {
    return { data: null, error: access.error };
  }
  const ext = file.name.split('.').pop() || 'jpg';
  const fileName = `${eventId}/${folder}/${Date.now()}.${ext}`;
  try {
    const { data, error } = await supabase.storage
      .from('landing-page-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true,
      });
    if (error) throw error;
    const { data: urlData } = supabase.storage.from('landing-page-images').getPublicUrl(data.path);
    return { data: { url: urlData.publicUrl, path: data.path }, error: null };
  } catch (error) {
    console.error('[LandingPageService] uploadLandingPageImage error:', error);
    return { data: null, error };
  }
}

/**
 * Get complete landing page data for an event using RPC function
 * @param {string} eventId - The event ID
 * @param {{ useAnon?: boolean }} [options]
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function getEventLandingData(eventId, options = {}) {
  try {
    const client = options.useAnon ? supabaseAnon : supabase;
    const { data, error } = await client
      .rpc('get_event_landing_data', { p_event_id: eventId });

    if (error) throw error;
    const normalized = normalizeLandingRpcData(data);
    return {
      data: {
        ...data,
        ...normalized,
        event: data?.event ?? null
      },
      error: null
    };
  } catch (error) {
    console.error('[LandingPageService] getEventLandingData error:', error);
    return { data: null, error };
  }
}

/**
 * Get all public landing page data for an event: event, sessions (primary timing),
 * RPC landing content (objectives, FAQs, testimonials, bonuses), and instructors if available.
 * Use this for the public landing page; sessions are the primary source for dates/times.
 * @param {string} eventId - The event ID
 * @returns {Promise<{data: { event: Object, sessions: Array, learning_objectives: Array, faqs: Array, testimonials: Array, bonuses: Array, instructors: Array }|null, error: Error|null}>}
 */
/**
 * Look up a sharing link by ref slug (public, anon-safe).
 * @param {string} eventId
 * @param {string} refSlug
 */
export async function getPublicSharingLinkByRef(eventId, refSlug) {
  if (!refSlug) return { data: null, error: null };
  try {
    const normRef = String(refSlug).trim().toLowerCase();
    let query = supabaseAnon
      .from('agent_sharing_links')
      .select('id, agent_id, slug, event_id')
      .eq('slug', normRef);
    if (eventId) query = query.eq('event_id', eventId);
    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getPublicSharingLinkByRef error:', error);
    return { data: null, error };
  }
}

/**
 * Fetch agent display fields for public landing pages (anon-safe RPC).
 * @param {string} agentId
 */
export async function getPublicLandingAgent(agentId) {
  if (!agentId) return { data: null, error: null };
  try {
    const { data, error } = await supabaseAnon.rpc('get_public_landing_agent', {
      p_agent_id: agentId
    });
    if (error) throw error;
    const row = data && typeof data === 'object' ? data : null;
    if (!row?.full_name && !row?.id) {
      return { data: null, error: null };
    }
    return { data: row, error: null };
  } catch (error) {
    console.error('[LandingPageService] getPublicLandingAgent error:', error);
    return { data: null, error };
  }
}

/**
 * Resolve agent for landing: ?ref= slug → sharing link → user, else event.agent_id.
 * @param {{ eventId?: string, refSlug?: string|null, eventAgentId?: string|null }} params
 */
export async function resolvePublicLandingAgent({ eventId, refSlug, eventAgentId }) {
  let agentId = null;
  if (refSlug) {
    const { data: link } = await getPublicSharingLinkByRef(eventId, refSlug);
    agentId = link?.agent_id ?? null;
  }
  if (!agentId && eventAgentId) {
    agentId = eventAgentId;
  }
  if (!agentId) return { data: null, error: null };
  return getPublicLandingAgent(agentId);
}

export async function getPublicLandingPageData(eventId) {
  if (!eventId) {
    return { data: null, error: { message: 'Event ID is required.', code: 'invalid_input' } };
  }

  try {
    const [eventRes, sessionsRes, rpcRes, instructorsRes, landingPageRes] = await Promise.all([
      eventsService.getPublicEventById(eventId),
      eventsService.getPublicEventSessions(eventId),
      getEventLandingData(eventId, { useAnon: true }),
      supabaseAnon.rpc('get_event_instructors', { p_event_id: eventId }).then(({ data, error }) => ({ data: error ? null : (data ?? []), error })),
      getPublicEventLandingPage(eventId)
    ]);

    if (eventRes.error || !eventRes.data) {
      return { data: null, error: eventRes.error || { message: 'Event not found.' } };
    }

    const event = eventRes.data;
    const sessionsFromApi = sessionsRes.error ? [] : (sessionsRes.data ?? []);
    const rpcNormalized = normalizeLandingRpcData(rpcRes.data || {});
    const instructors = rpcNormalized.instructors.length > 0
      ? rpcNormalized.instructors
      : (Array.isArray(instructorsRes?.data) ? instructorsRes.data : []);
    const landingPage = landingPageRes.data;
    const flatSessionsFromApi = normalizeLandingSessions(sessionsFromApi);
    const sessions =
      flatSessionsFromApi.length > 0
        ? flatSessionsFromApi
        : rpcNormalized.sessions;

    const merged = {
      event,
      sessions,
      learning_objectives: rpcNormalized.learning_objectives,
      faqs: rpcNormalized.faqs,
      testimonials: rpcNormalized.testimonials,
      bonuses: rpcNormalized.bonuses,
      instructors,
      landing_page: landingPage
    };

    return { data: merged, error: null };
  } catch (error) {
    console.error('[LandingPageService] getPublicLandingPageData error:', error);
    return { data: null, error };
  }
}

// ============ Learning Objectives ============

/**
 * Get all learning objectives for an event
 * @param {string} eventId - The event ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getEventLearningObjectives(eventId) {
  try {
    const { data, error } = await supabase
      .from('event_learning_objectives')
      .select('*')
      .eq('event_id', eventId)
      .order('display_order');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getEventLearningObjectives error:', error);
    return { data: null, error };
  }
}

/**
 * Create a learning objective
 * @param {Object} objectiveData - The objective data
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function createLearningObjective(objectiveData) {
  try {
    const { data, error } = await supabase
      .from('event_learning_objectives')
      .insert([{
        ...objectiveData,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] createLearningObjective error:', error);
    return { data: null, error };
  }
}

/**
 * Update a learning objective
 * @param {string} objectiveId - The objective ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function updateLearningObjective(objectiveId, updates) {
  try {
    const { data, error } = await supabase
      .from('event_learning_objectives')
      .update(updates)
      .eq('id', objectiveId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] updateLearningObjective error:', error);
    return { data: null, error };
  }
}

/**
 * Delete a learning objective
 * @param {string} objectiveId - The objective ID
 * @returns {Promise<{error: Error|null}>}
 */
export async function deleteLearningObjective(objectiveId) {
  try {
    const { error } = await supabase
      .from('event_learning_objectives')
      .delete()
      .eq('id', objectiveId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('[LandingPageService] deleteLearningObjective error:', error);
    return { error };
  }
}

/**
 * Reorder learning objectives
 * @param {string} eventId - The event ID
 * @param {Array<{id: string, display_order: number}>} orderedItems - The new order
 * @returns {Promise<{error: Error|null}>}
 */
export async function reorderLearningObjectives(eventId, orderedItems) {
  try {
    for (const item of orderedItems) {
      const { error } = await supabase
        .from('event_learning_objectives')
        .update({ display_order: item.display_order })
        .eq('id', item.id)
        .eq('event_id', eventId);

      if (error) throw error;
    }
    return { error: null };
  } catch (error) {
    console.error('[LandingPageService] reorderLearningObjectives error:', error);
    return { error };
  }
}

// ============ FAQs ============

/**
 * Get all FAQs for an event
 * @param {string} eventId - The event ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getEventFAQs(eventId) {
  try {
    const { data, error } = await supabase
      .from('event_faqs')
      .select('*')
      .eq('event_id', eventId)
      .order('display_order');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getEventFAQs error:', error);
    return { data: null, error };
  }
}

/**
 * Create an FAQ
 * @param {Object} faqData - The FAQ data
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function createFAQ(faqData) {
  try {
    const { data, error } = await supabase
      .from('event_faqs')
      .insert([{
        ...faqData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] createFAQ error:', error);
    return { data: null, error };
  }
}

/**
 * Update an FAQ
 * @param {string} faqId - The FAQ ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function updateFAQ(faqId, updates) {
  try {
    const { data, error } = await supabase
      .from('event_faqs')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', faqId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] updateFAQ error:', error);
    return { data: null, error };
  }
}

/**
 * Delete an FAQ
 * @param {string} faqId - The FAQ ID
 * @returns {Promise<{error: Error|null}>}
 */
export async function deleteFAQ(faqId) {
  try {
    const { error } = await supabase
      .from('event_faqs')
      .delete()
      .eq('id', faqId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('[LandingPageService] deleteFAQ error:', error);
    return { error };
  }
}

/**
 * Reorder FAQs
 * @param {string} eventId - The event ID
 * @param {Array<{id: string, display_order: number}>} orderedItems - The new order
 * @returns {Promise<{error: Error|null}>}
 */
export async function reorderFAQs(eventId, orderedItems) {
  try {
    for (const item of orderedItems) {
      const { error } = await supabase
        .from('event_faqs')
        .update({ display_order: item.display_order })
        .eq('id', item.id)
        .eq('event_id', eventId);

      if (error) throw error;
    }
    return { error: null };
  } catch (error) {
    console.error('[LandingPageService] reorderFAQs error:', error);
    return { error };
  }
}

// ============ Testimonials ============

/**
 * Get all testimonials for an event (including global testimonials)
 * @param {string} eventId - The event ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getEventTestimonials(eventId) {
  try {
    const { data, error } = await supabase
      .from('event_testimonials')
      .select('*')
      .or(`event_id.eq.${eventId},event_id.is.null`)
      .order('featured', { ascending: false })
      .order('display_order');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getEventTestimonials error:', error);
    return { data: null, error };
  }
}

/**
 * Get only event-specific testimonials
 * @param {string} eventId - The event ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getEventSpecificTestimonials(eventId) {
  try {
    const { data, error } = await supabase
      .from('event_testimonials')
      .select('*')
      .eq('event_id', eventId)
      .order('featured', { ascending: false })
      .order('display_order');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getEventSpecificTestimonials error:', error);
    return { data: null, error };
  }
}

/**
 * Get global testimonials (not tied to any event)
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getGlobalTestimonials() {
  try {
    const { data, error } = await supabase
      .from('event_testimonials')
      .select('*')
      .is('event_id', null)
      .order('featured', { ascending: false })
      .order('display_order');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getGlobalTestimonials error:', error);
    return { data: null, error };
  }
}

/**
 * Create a testimonial
 * @param {Object} testimonialData - The testimonial data
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function createTestimonial(testimonialData) {
  try {
    const { data, error } = await supabase
      .from('event_testimonials')
      .insert([{
        ...testimonialData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] createTestimonial error:', error);
    return { data: null, error };
  }
}

/**
 * Update a testimonial
 * @param {string} testimonialId - The testimonial ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function updateTestimonial(testimonialId, updates) {
  try {
    const { data, error } = await supabase
      .from('event_testimonials')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', testimonialId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] updateTestimonial error:', error);
    return { data: null, error };
  }
}

/**
 * Delete a testimonial
 * @param {string} testimonialId - The testimonial ID
 * @returns {Promise<{error: Error|null}>}
 */
export async function deleteTestimonial(testimonialId) {
  try {
    const { error } = await supabase
      .from('event_testimonials')
      .delete()
      .eq('id', testimonialId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('[LandingPageService] deleteTestimonial error:', error);
    return { error };
  }
}

/**
 * Toggle testimonial featured status
 * @param {string} testimonialId - The testimonial ID
 * @param {boolean} featured - Whether to feature the testimonial
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function toggleTestimonialFeatured(testimonialId, featured) {
  try {
    const { data, error } = await supabase
      .from('event_testimonials')
      .update({
        featured,
        updated_at: new Date().toISOString()
      })
      .eq('id', testimonialId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] toggleTestimonialFeatured error:', error);
    return { data: null, error };
  }
}

// ============ Bonuses ============

/**
 * Get all bonuses for an event
 * @param {string} eventId - The event ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getEventBonuses(eventId) {
  try {
    const { data, error } = await supabase
      .from('event_bonuses')
      .select('*')
      .eq('event_id', eventId)
      .order('display_order');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] getEventBonuses error:', error);
    return { data: null, error };
  }
}

/**
 * Create a bonus
 * @param {Object} bonusData - The bonus data
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function createBonus(bonusData) {
  try {
    const row = {
      event_id: bonusData.event_id,
      bonus_title: bonusData.bonus_title ?? '',
      bonus_description: bonusData.bonus_description ?? null,
      bonus_type: bonusData.bonus_type ?? null,
      value_description: bonusData.value_description ?? null,
      icon_name: bonusData.icon_name ?? null,
      display_order: bonusData.display_order ?? 0,
      access_url: bonusData.access_url ?? null,
      delivery_method: bonusData.delivery_method ?? null,
      created_at: new Date().toISOString()
    };
    const { data, error } = await supabase
      .from('event_bonuses')
      .insert([row])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] createBonus error:', error);
    return { data: null, error };
  }
}

/**
 * Update a bonus
 * @param {string} bonusId - The bonus ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
const EVENT_BONUS_UPDATE_KEYS = [
  'bonus_title', 'bonus_description', 'bonus_type', 'value_description',
  'icon_name', 'access_url', 'delivery_method'
];

export async function updateBonus(bonusId, updates) {
  try {
    const row = Object.fromEntries(
      EVENT_BONUS_UPDATE_KEYS
        .filter((k) => k in updates)
        .map((k) => [k, updates[k]])
    );
    const { data, error } = await supabase
      .from('event_bonuses')
      .update(row)
      .eq('id', bonusId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[LandingPageService] updateBonus error:', error);
    return { data: null, error };
  }
}

/**
 * Delete a bonus
 * @param {string} bonusId - The bonus ID
 * @returns {Promise<{error: Error|null}>}
 */
export async function deleteBonus(bonusId) {
  try {
    const { error } = await supabase
      .from('event_bonuses')
      .delete()
      .eq('id', bonusId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('[LandingPageService] deleteBonus error:', error);
    return { error };
  }
}

/**
 * Reorder bonuses
 * @param {string} eventId - The event ID
 * @param {Array<{id: string, display_order: number}>} orderedItems - The new order
 * @returns {Promise<{error: Error|null}>}
 */
export async function reorderBonuses(eventId, orderedItems) {
  try {
    for (const item of orderedItems) {
      const { error } = await supabase
        .from('event_bonuses')
        .update({ display_order: item.display_order })
        .eq('id', item.id)
        .eq('event_id', eventId);

      if (error) throw error;
    }
    return { error: null };
  } catch (error) {
    console.error('[LandingPageService] reorderBonuses error:', error);
    return { error };
  }
}
